// BureauManager.jsx — Onglet "Bureau" dans /admin-asso
// Permet au PRÉSIDENT de désigner secrétaire, trésorier, vice-président parmi les adhérents.
// Affiche aussi le bureau actuel à tous les rôles bureau (read-only sauf président).
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { toast } from 'sonner';
import { Crown, Shield, Coins, FileText, User, Plus, X, Loader2 } from 'lucide-react';

const ROLE_META = {
  president:      { label: 'Président',        icon: Crown,    color: '#f59e0b', desc: 'Voit et gère tout. Seul à désigner les rôles bureau.' },
  vice_president: { label: 'Vice-président',   icon: Shield,   color: '#8b5cf6', desc: 'Assiste le président. Mêmes droits sauf désignation.' },
  secretaire:    { label: 'Secrétaire',       icon: FileText, color: '#0ea5e9', desc: 'Gère adhésions et événements. Voit la trésorerie en lecture seule.' },
  tresorier:     { label: 'Trésorier',        icon: Coins,    color: '#10b981', desc: 'Saisit dépenses/recettes, fait rapprochement bancaire. Ne voit pas la liste des adhésions.' },
  membre:        { label: 'Membre',           icon: User,     color: '#64748b', desc: 'Adhérent simple. Accède à son espace personnel.' },
};

export default function BureauManager({ canDesigner }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoting, setPromoting] = useState(null); // {userId, newRole}
  const [picker, setPicker] = useState(null); // userId quand on ouvre le selector de rôle

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/assos/users');
      setUsers(data);
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const assignRole = async (userId, newRole) => {
    setPromoting({ userId, newRole });
    try {
      await api.patch(`/assos/users/${userId}/role`, { role: newRole });
      toast.success(`Rôle mis à jour : ${ROLE_META[newRole].label}`);
      setPicker(null);
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || 'Erreur lors de la désignation');
    } finally { setPromoting(null); }
  };

  const bureau = users.filter((u) => ['president', 'vice_president', 'secretaire', 'tresorier'].includes(u.role));
  const adherents = users.filter((u) => u.role === 'membre');

  if (loading) {
    return (
      <div className="py-16 grid place-items-center" data-testid="bureau-loading">
        <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="bureau-manager">
      {/* Bureau actuel */}
      <section>
        <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Bureau actuel</h3>
        <p className="text-xs text-slate-500 mb-4">{bureau.length} membre{bureau.length > 1 ? 's' : ''} du bureau</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="bureau-grid">
          {bureau.length === 0 ? (
            <div className="col-span-2 text-center py-10 text-sm text-slate-500 border border-dashed border-slate-200 rounded-lg">
              Aucun membre du bureau encore désigné.
            </div>
          ) : bureau.map((u) => {
            const meta = ROLE_META[u.role];
            const Ic = meta.icon;
            return (
              <div key={u.id} className="card flex items-center gap-3 !p-3" data-testid={`bureau-card-${u.id}`}>
                <div className="w-10 h-10 rounded-full grid place-items-center shrink-0" style={{ background: meta.color + '20' }}>
                  <Ic className="w-5 h-5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-900 text-sm truncate">{u.prenom || ''} {u.nom || ''}</div>
                  <div className="text-xs text-slate-500 truncate">{u.email}</div>
                </div>
                <div className="text-right shrink-0">
                  <span className="badge badge-primary" style={{ background: meta.color + '15', color: meta.color, borderColor: meta.color + '30' }}>{meta.label}</span>
                  {canDesigner && (
                    <button
                      onClick={() => setPicker(u.id)}
                      className="block mt-1.5 ml-auto text-[10px] text-slate-400 hover:text-slate-700"
                      data-testid={`bureau-change-${u.id}`}
                    >Changer →</button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Adhérents — désignation */}
      {canDesigner && (
        <section>
          <h3 className="font-display text-lg font-bold text-slate-900 mb-1">Promouvoir un adhérent</h3>
          <p className="text-xs text-slate-500 mb-4">
            Désignez vos secrétaire, trésorier ou vice-président parmi les adhérents actifs.
          </p>

          {adherents.length === 0 ? (
            <div className="card text-center py-10 text-sm text-slate-500">
              Aucun adhérent à promouvoir pour le moment.
            </div>
          ) : (
            <div className="card !p-0 overflow-hidden" data-testid="adherents-list">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr className="text-xs text-slate-500 uppercase tracking-wider">
                    <th className="text-left px-4 py-2.5 font-bold">Adhérent</th>
                    <th className="text-left px-4 py-2.5 font-bold hidden sm:table-cell">Email</th>
                    <th className="text-right px-4 py-2.5 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {adherents.map((u) => (
                    <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`adherent-row-${u.id}`}>
                      <td className="px-4 py-2.5">
                        <div className="font-medium text-slate-900">{u.prenom || ''} {u.nom || ''}</div>
                        <div className="text-xs text-slate-500 sm:hidden">{u.email}</div>
                      </td>
                      <td className="px-4 py-2.5 text-slate-600 hidden sm:table-cell">{u.email}</td>
                      <td className="px-4 py-2.5 text-right">
                        <button
                          onClick={() => setPicker(u.id)}
                          className="btn-secondary !py-1 !px-2.5 text-xs flex items-center gap-1 ml-auto"
                          data-testid={`promote-btn-${u.id}`}
                        >
                          <Plus className="w-3 h-3" /> Désigner
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      {/* Modal sélection de rôle */}
      {picker && (
        <RolePicker
          user={users.find((u) => u.id === picker)}
          currentRole={users.find((u) => u.id === picker)?.role}
          onPick={(role) => assignRole(picker, role)}
          onClose={() => setPicker(null)}
          isPromoting={promoting?.userId === picker ? promoting.newRole : null}
        />
      )}

      {/* Légende permissions */}
      <section className="card !bg-slate-50/50">
        <h4 className="font-bold text-sm text-slate-900 mb-3">Permissions par rôle</h4>
        <div className="space-y-2 text-xs">
          {Object.entries(ROLE_META).map(([role, meta]) => {
            const Ic = meta.icon;
            return (
              <div key={role} className="flex items-start gap-2.5">
                <div className="w-7 h-7 rounded-full grid place-items-center shrink-0 mt-0.5" style={{ background: meta.color + '15' }}>
                  <Ic className="w-3.5 h-3.5" style={{ color: meta.color }} />
                </div>
                <div className="flex-1">
                  <span className="font-bold" style={{ color: meta.color }}>{meta.label}</span>
                  <span className="text-slate-600"> — {meta.desc}</span>
                </div>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

function RolePicker({ user, currentRole, onPick, onClose, isPromoting }) {
  return (
    <div className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm grid place-items-center p-3 sm:p-6 fade-in" onClick={onClose}>
      <div className="card max-w-md w-full" onClick={(e) => e.stopPropagation()} data-testid="role-picker-modal">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="font-display text-base font-bold text-slate-900">Désigner un rôle</h3>
            <p className="text-xs text-slate-500 mt-0.5">{user?.prenom} {user?.nom} · {user?.email}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700" aria-label="Fermer"><X className="w-5 h-5" /></button>
        </div>
        <div className="space-y-2 mt-4" data-testid="role-options">
          {Object.entries(ROLE_META).map(([role, meta]) => {
            const Ic = meta.icon;
            const isCurrent = currentRole === role;
            const isLoading = isPromoting === role;
            return (
              <button
                key={role}
                onClick={() => !isCurrent && !isLoading && onPick(role)}
                disabled={isCurrent || !!isPromoting}
                className={`w-full flex items-center gap-3 p-3 rounded-lg border transition-all text-left ${
                  isCurrent
                    ? 'bg-slate-100 border-slate-300 cursor-not-allowed opacity-60'
                    : 'border-slate-200 hover:border-slate-400 hover:bg-slate-50 cursor-pointer'
                }`}
                data-testid={`role-option-${role}`}
              >
                <div className="w-9 h-9 rounded-full grid place-items-center shrink-0" style={{ background: meta.color + '20' }}>
                  {isLoading ? <Loader2 className="w-4 h-4 animate-spin" style={{ color: meta.color }} /> : <Ic className="w-4 h-4" style={{ color: meta.color }} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-sm text-slate-900 flex items-center gap-2">
                    {meta.label}
                    {isCurrent && <span className="text-[10px] text-slate-400 font-normal">(actuel)</span>}
                  </div>
                  <div className="text-[11px] text-slate-500 leading-relaxed">{meta.desc}</div>
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
